import React, { useState, useEffect } from 'react';
import logo from '../assets/logo.png';
import logo2 from '../assets/logo2.png';
import './CSS/Login.css'; // Ensure styles exist for .logo and .logo2

// Helper function to highlight text
const highlightText = (text, query) => {
  if (!query) return text;
  const parts = String(text).split(new RegExp(`(${query})`, 'gi'));
  return parts.map((part, index) =>
    part.toLowerCase() === (query || '').toLowerCase() ? (
      <span key={index} style={{ backgroundColor: 'yellow', fontWeight: 'bold' }}>
        {part}
      </span>
    ) : (
      part
    )
  );
};

// Utility: truncate long text to n characters (preserve whole words)
const truncate = (text, n = 200) => {
  if (!text) return '';
  if (text.length <= n) return text;
  const trimmed = text.slice(0, n);
  const lastSpace = trimmed.lastIndexOf(' ');
  return trimmed.slice(0, lastSpace > 0 ? lastSpace : n) + '...';
};

// Search Bar Component
const SearchBar = ({ onSearch }) => (
  <div style={{ padding: '10px', textAlign: 'center' }}>
    <img className='logo' src={logo} alt="Logo" />
    <br />
    <input
      type="text"
      placeholder="Search /blocks..."
      onChange={(e) => onSearch(e.target.value)}
      style={{
        width: '60%',
        padding: '10px',
        fontSize: '16px',
        borderRadius: '5px',
        border: '1px solid #ccc',
        marginBottom: '20px',
      }}
    />
  </div>
);

// Search Results Component with expand/collapse behavior
const SearchResults = ({ results, query }) => {
  // track expanded items: map 'blockIndex-dataIndex' => boolean for outputMap
  const [expandedFields, setExpandedFields] = useState({});
  // track expanded blocks to show full JSON
  const [expandedBlocks, setExpandedBlocks] = useState({});

  const toggleField = (key) =>
    setExpandedFields((s) => ({ ...s, [key]: !s[key] }));

  const toggleBlock = (key) =>
    setExpandedBlocks((s) => ({ ...s, [key]: !s[key] }));

  return (
    <div style={{ padding: '10px', textAlign: 'center' }}>
      {results.length > 0 ? (
        results.map((result, rIdx) => {
          const blockKey = `block-${rIdx}`;
          return (
            <div
              key={rIdx}
              style={{
                margin: '10px 0',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '5px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                textAlign: 'left',
                maxWidth: '900px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <p style={{ fontWeight: 'bold', fontSize: '18px', margin: 0 }}>
                    Hash: {highlightText(result.hash, query)}
                  </p>
                  <p style={{ margin: '5px 0', color: '#555' }}>
                    Timestamp: {highlightText(String(result.timestamp), query)}
                  </p>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <button
                    onClick={() => toggleBlock(blockKey)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      border: '1px solid #ccc',
                      background: '#fff',
                      cursor: 'pointer',
                      marginBottom: '6px'
                    }}
                  >
                    {expandedBlocks[blockKey] ? 'Hide full block' : 'Show full block'}
                  </button>
                  <div style={{ fontSize: '12px', color: '#888' }}>Results #{rIdx + 1}</div>
                </div>
              </div>

              {/* Display Data Details */}
              <div style={{ marginTop: '10px' }}>
                <h4 style={{ margin: '6px 0' }}>Data:</h4>
                {result.data && result.data.length > 0 ? (
                  result.data.map((item, i) => {
                    const fieldKey = `b${rIdx}-d${i}`;
                    const outputMapFull = item.outputMap || '';
                    const preview = truncate(outputMapFull, 300);

                    return (
                      <div key={i} style={{ marginBottom: '12px', padding: '8px', background: '#fafafa', borderRadius: '4px' }}>
                        <p style={{ margin: '4px 0' }}>ID: <strong>{highlightText(item.id, query)}</strong></p>

                        <p style={{ margin: '4px 0' }}>
                          Output Map:{" "}
                          {expandedFields[fieldKey] ? (
                            // Show full outputMap (with highlight)
                            <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {highlightText(outputMapFull, query)}
                            </span>
                          ) : (
                            // Show truncated preview (with highlight)
                            <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {highlightText(preview, query)}
                            </span>
                          )}
                        </p>

                        {/* If truncated, show toggle */}
                        {outputMapFull.length > 300 && (
                          <button
                            onClick={() => toggleField(fieldKey)}
                            style={{
                              marginTop: '6px',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #ccc',
                              background: '#fff',
                              cursor: 'pointer'
                            }}
                          >
                            {expandedFields[fieldKey] ? 'Show less' : 'Show more'}
                          </button>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p style={{ fontSize: '14px', color: '#999' }}>No data available</p>
                )}
              </div>

              {/* Full JSON block view */}
              {expandedBlocks[blockKey] && (
                <div style={{ marginTop: '12px', background: '#222', color: '#e6e6e6', padding: '10px', borderRadius: '6px', fontFamily: 'monospace', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                  {JSON.stringify(result, null, 2)}
                </div>
              )}
            </div>
          );
        })
      ) : (
        <p style={{ fontSize: '18px', color: '#555' }}>No results found in /blocks</p>
      )}
    </div>
  );
};

// Main Search Engine Component
const SearchEngine = () => {
  const [query, setQuery] = useState('');
  const [data, setData] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSearchData = async () => {
      try {
        const response = await fetch('/search-data'); // Backend fetch from external API
        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }
        const formattedData = await response.json();
        setData(formattedData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching search data:', err);
        setError('Failed to fetch search data.');
        setLoading(false);
      }
    };

    fetchSearchData();
  }, []);

  const handleSearch = (searchQuery) => {
    setQuery(searchQuery);

    if (searchQuery) {
      const lower = searchQuery.toLowerCase();
      const filteredResults = data.filter((block) =>
        (block.hash && block.hash.toLowerCase().includes(lower)) ||
        (String(block.timestamp) && String(block.timestamp).includes(searchQuery)) ||
        (Array.isArray(block.data) &&
          block.data.some((item) =>
            Object.values(item).some(
              (val) =>
                typeof val === 'string' &&
                val.toLowerCase().includes(lower)
            )
          ))
      );
      setResults(filteredResults);
    } else {
      setResults([]);
    }
  };

  if (loading) {
    return <p style={{ textAlign: 'center', fontSize: '18px' }}>Loading search data...</p>;
  }

  if (error) {
    return <p style={{ textAlign: 'center', fontSize: '18px', color: 'red' }}>{error}</p>;
  }

  return (
    <div>
      <h1 style={{ textAlign: 'center', marginTop: '20px' }}>Search /blocks</h1>
      <SearchBar onSearch={handleSearch} />
      <SearchResults results={results} query={query} />
      <br />
      <div style={{ textAlign: 'center', marginTop: '10px' }}>Powered by</div>
      <div style={{ textAlign: 'center' }}>
        <img className='logo2' src={logo2} alt="Logo 2" style={{ maxWidth: '160px', marginTop: '6px' }} />
      </div>
      <div style={{ textAlign: 'center', color: '#444', marginTop: '6px' }}>New Wave Consulting Group</div>
    </div>
  );
};

export default SearchEngine;
